import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { EngagementProvider as EngagementProviderWithoutConfig } from '@atlassiansox/engagekit-ts';

import { ProductIds } from '@atlassian/ipm-choreographer';

import { getAnalyticsWebClient, getWorkspaceId } from '@confluence/analytics-web-client';
import { useAnalyticsEnvironment } from '@confluence/choreographer-services/entry-points/useAnalyticsEnvironment';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';

export function EngagementProviderComponent(props: PropsWithChildren<{}>) {
	const { cloudId, locale, userId, activationId, environment } = useSessionData();

	const workspaceId = getWorkspaceId(cloudId, activationId);

	if (sessionStorage.getItem(keys.DISABLE_ENGAGEMENT_FOR_TESTS) === 'true') {
		return <>{props.children}</>;
	}

	const config = {
		cloudId,
		aaid: userId as string,
		locale,
		product: 'confluence',
		stargateUrl: '/gateway/api',
		workspaceId,
		isFedRAMPEnvironment: false,
	};

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const envType = useAnalyticsEnvironment(environment);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const coordinationOptions = useMemo(
		() => ({
			enableChoreographer: true,
			envType,
			additionalData: { sourceProductId: ProductIds.CONFLUENCE },
		}),
		[envType],
	);

	return (
		<EngagementProviderWithoutConfig
			{...props}
			// eslint-disable-next-line react/no-children-prop
			children={<>{props.children}</>}
			config={config}
			analyticsClientInstance={getAnalyticsWebClient()}
			coordinationOptions={coordinationOptions}
		/>
	);
}

EngagementProviderComponent.propTypes = {
	children: PropTypes.element,
	locale: PropTypes.string,
};
